// A circle's alerts: this browser's Web Push subscription, the user's
// arrive/leave rules per member × place (null ids are "Everyone" / "Any
// place") and their low-battery, location-stale, location-off, speed and check-in alerts per member.
import type { Schemas } from "@10twenty/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { api, ApiError, errorMessage, publicApi, unwrap } from "../api";
import { useAuth } from "../auth/AuthContext";
import {
  ALERT_MODES,
  cellMode,
  inheritedMemberAlert,
  inheritedMode,
  inheritedSpeed,
  memberAlertDefault,
  MEMBER_ALERT_LABELS,
  MEMBER_ALERTS,
  memberAlertOf,
  MODE_LABELS,
  ruleFor,
  speedChoices,
  speedLabel,
  speedOf,
  withCell,
  withMemberAlert,
  withSpeed,
  type AlertMode,
  type MemberAlert,
} from "../notifications/notifications";
import { disableWebPush, enableWebPush, webPushStatus, type WebPushStatus } from "../notifications/webPush";
import { sortPlaces } from "../places/places";
import type { Units } from "../units";
import { useUnits } from "../useUnits";

type Preference = Schemas["NotificationPreference"];
const PREFERENCES_KEY = ["notification-preferences"];
/** Attempts at a conditional replace before giving up on concurrent edits. */
const SAVE_ATTEMPTS = 3;

/**
 * Applies one edit to the freshly read rules and replaces them only if nobody
 * changed them meanwhile (If-Match), so other circles' rules and edits from
 * other devices survive; a lost race rereads and reapplies.
 */
async function saveRules(edit: (rules: Preference[]) => Preference[]) {
  for (let attempt = 1; ; attempt++) {
    const read = await api.GET("/me/notification-preferences");
    const etag = read.response.headers.get("ETag");
    const body = edit(unwrap(read));
    try {
      return unwrap(
        await api.PUT("/me/notification-preferences", {
          body,
          params: { header: etag ? { "If-Match": etag } : {} },
        }),
      );
    } catch (error) {
      if (!(error instanceof ApiError && error.status === 412) || attempt >= SAVE_ATTEMPTS) throw error;
    }
  }
}

export function NotificationsPage() {
  const circleId = useParams().circleId ?? "";
  return <NotificationsView key={circleId} circleId={circleId} />;
}

function NotificationsView({ circleId }: { circleId: string }) {
  const { session } = useAuth();
  const circle = useQuery({
    queryKey: ["circle", circleId],
    queryFn: async () => unwrap(await api.GET("/circles/{circleId}", { params: { path: { circleId } } })),
  });
  const members = useQuery({
    queryKey: ["circle", circleId, "members"],
    queryFn: async () => unwrap(await api.GET("/circles/{circleId}/members", { params: { path: { circleId } } })),
  });
  const places = useQuery({
    queryKey: ["circle", circleId, "places"],
    queryFn: async () => unwrap(await api.GET("/circles/{circleId}/places", { params: { path: { circleId } } })),
  });
  const preferences = useQuery({
    queryKey: PREFERENCES_KEY,
    queryFn: async () => unwrap(await api.GET("/me/notification-preferences")),
  });

  const error = circle.error ?? members.error ?? places.error ?? preferences.error;
  return (
    <div className="stack notifications-page">
      <p>
        <Link to={`/circles/${circleId}`}>← {circle.data?.name ?? "Circle"}</Link>
      </p>
      <h1>Alerts</h1>
      <BrowserPushCard />
      <section className="card stack">
        <h2>Arrivals and departures</h2>
        <p className="muted">
          Choose who and which places you hear about. A specific choice wins over “Everyone” and “Any place”; “Default”
          follows them. You never get alerts about yourself, or about members who have paused sharing.
        </p>
        {error && <p className="error">{errorMessage(error)}</p>}
        {!error && (!members.data || !places.data || !preferences.data) && <p className="muted">Loading…</p>}
        {members.data && places.data && preferences.data && session && (
          <AlertGrid
            circleId={circleId}
            members={members.data.filter((m) => m.user_id !== session.userId)}
            places={sortPlaces(places.data)}
            rules={preferences.data}
          />
        )}
      </section>
      <section className="card stack">
        <h2>Member alerts</h2>
        <p className="muted">
          Hear when someone's battery falls to 15 % while not charging (once until they charge), when their phone has
          sent no location for an hour, when they drive too fast, or when they check in.{" "}
          {circle.data?.kind === "friends"
            ? "In a friends circle check-ins are off unless you turn them on."
            : "Check-ins are on unless you turn them off."}{" "}
          A choice for a member wins over “Everyone”.
        </p>
        {!error && (!circle.data || !members.data || !preferences.data) && <p className="muted">Loading…</p>}
        {circle.data && members.data && preferences.data && session && (
          <MemberAlertGrid
            circleId={circleId}
            kind={circle.data.kind}
            members={members.data.filter((m) => m.user_id !== session.userId)}
            rules={preferences.data}
          />
        )}
      </section>
    </div>
  );
}

function useSaveRules() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: saveRules,
    onSuccess: (stored) => queryClient.setQueryData(PREFERENCES_KEY, stored),
    onError: () => queryClient.invalidateQueries({ queryKey: PREFERENCES_KEY }),
  });
}

function AlertGrid({
  circleId,
  members,
  places,
  rules,
}: {
  circleId: string;
  members: Schemas["Member"][];
  places: Schemas["Place"][];
  rules: Preference[];
}) {
  const save = useSaveRules();

  const rows: { id: string | null; name: string }[] = [
    { id: null, name: "Everyone" },
    ...members.map((m) => ({ id: m.user_id, name: m.name })),
  ];
  const columns: { id: string | null; name: string }[] = [
    { id: null, name: "Any place" },
    ...places.map((p) => ({ id: p.id, name: p.name })),
  ];
  return (
    <>
      {places.length === 0 && (
        <p className="muted">
          This circle has no places yet. <Link to={`/circles/${circleId}/places`}>Add places</Link> such as home or
          school to get alerts.
        </p>
      )}
      <div className="alert-grid-scroll">
        <table className="alert-grid">
          <thead>
            <tr>
              <th scope="col">
                <span className="visually-hidden">Member</span>
              </th>
              {columns.map((column) => (
                <th scope="col" key={column.id ?? "any"}>
                  {column.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id ?? "everyone"}>
                <th scope="row">{row.name}</th>
                {columns.map((column) => {
                  const mode = cellMode(rules, circleId, row.id, column.id);
                  // A rule setting only arrivals or departures shows its resolved mode as a
                  // separate entry, so choosing that mode in the menu still stores both flags.
                  const own = ruleFor(rules, circleId, row.id, column.id);
                  const partial = !!own && (own.on_arrive == null) !== (own.on_leave == null);
                  const root = row.id === null && column.id === null;
                  const inherited = inheritedMode(rules, circleId, row.id, column.id);
                  return (
                    <td key={column.id ?? "any"}>
                      <select
                        aria-label={`${row.name}, ${column.name}`}
                        value={partial ? "partial" : (mode ?? (root ? "off" : "default"))}
                        disabled={save.isPending}
                        onChange={(event) => {
                          const value = event.target.value;
                          const next = value === "default" ? null : (value as AlertMode);
                          save.mutate((all) => withCell(all, circleId, row.id, column.id, next));
                        }}
                      >
                        {partial && mode && (
                          <option value="partial" disabled>
                            {MODE_LABELS[mode]} (partly default)
                          </option>
                        )}
                        {!root && <option value="default">Default ({MODE_LABELS[inherited].toLowerCase()})</option>}
                        {ALERT_MODES.map((mode) => (
                          <option key={mode} value={mode}>
                            {MODE_LABELS[mode]}
                          </option>
                        ))}
                      </select>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {save.error && (
        <p className="error">
          {errorMessage(save.error, { 400: "That member or place is no longer in this circle. Reload and try again." })}
        </p>
      )}
    </>
  );
}

function MemberAlertGrid({
  circleId,
  kind,
  members,
  rules,
}: {
  circleId: string;
  kind: Schemas["CircleKind"];
  members: Schemas["Member"][];
  rules: Preference[];
}) {
  const save = useSaveRules();
  const rows: { id: string | null; name: string }[] = [
    { id: null, name: "Everyone" },
    ...members.map((m) => ({ id: m.user_id, name: m.name })),
  ];
  const onOff = (value: boolean) => (value ? "On" : "Off");
  const units = useUnits();
  return (
    <>
      <div className="alert-grid-scroll">
        <table className="alert-grid">
          <thead>
            <tr>
              <th scope="col">
                <span className="visually-hidden">Member</span>
              </th>
              {MEMBER_ALERTS.map((alert) => (
                <th scope="col" key={alert}>
                  {MEMBER_ALERT_LABELS[alert]}
                </th>
              ))}
              <th scope="col">Speeding</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id ?? "everyone"}>
                <th scope="row">{row.name}</th>
                {MEMBER_ALERTS.map((alert: MemberAlert) => {
                  const value = memberAlertOf(rules, circleId, row.id, alert);
                  const root = row.id === null;
                  return (
                    <td key={alert}>
                      <select
                        aria-label={`${row.name}, ${MEMBER_ALERT_LABELS[alert]}`}
                        value={
                          value === null
                            ? root
                              ? onOff(memberAlertDefault(alert, kind)).toLowerCase()
                              : "default"
                            : value
                              ? "on"
                              : "off"
                        }
                        disabled={save.isPending}
                        onChange={(event) => {
                          const choice = event.target.value;
                          const next = choice === "default" ? null : choice === "on";
                          save.mutate((all) => withMemberAlert(all, circleId, row.id, alert, next, kind));
                        }}
                      >
                        {!root && (
                          <option value="default">
                            Default ({onOff(inheritedMemberAlert(rules, circleId, alert, kind)).toLowerCase()})
                          </option>
                        )}
                        <option value="on">On</option>
                        <option value="off">Off</option>
                      </select>
                    </td>
                  );
                })}
                <td>
                  <SpeedSelect
                    circleId={circleId}
                    memberId={row.id}
                    name={row.name}
                    rules={rules}
                    units={units}
                    disabled={save.isPending}
                    onChange={(kph) => save.mutate((all) => withSpeed(all, circleId, row.id, kph))}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {save.error && (
        <p className="error">
          {errorMessage(save.error, { 400: "That member is no longer in this circle. Reload and try again." })}
        </p>
      )}
    </>
  );
}

/** A row's speed alert: Default (members only), Off or a threshold. */
function SpeedSelect({
  circleId,
  memberId,
  name,
  rules,
  units,
  disabled,
  onChange,
}: {
  circleId: string;
  memberId: string | null;
  name: string;
  rules: Preference[];
  units: Units;
  disabled: boolean;
  onChange: (kph: number | null) => void;
}) {
  const value = speedOf(rules, circleId, memberId);
  const root = memberId === null;
  return (
    <select
      aria-label={`${name}, Speeding`}
      value={value === null ? (root ? "0" : "default") : String(value)}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value === "default" ? null : Number(event.target.value))}
    >
      {!root && (
        <option value="default">Default ({speedLabel(inheritedSpeed(rules, circleId), units).toLowerCase()})</option>
      )}
      <option value="0">Off</option>
      {speedChoices(units, value).map((kph) => (
        <option key={kph} value={String(kph)}>
          {speedLabel(kph, units)}
        </option>
      ))}
    </select>
  );
}

const STATUS_TEXT: Record<WebPushStatus, string> = {
  unsupported: "This browser can't receive push alerts.",
  disabled: "This server isn't set up for browser alerts.",
  denied: "Notifications are blocked for this site. Allow them in your browser's site settings.",
  off: "Alerts aren't sent to this browser.",
  on: "This browser receives your alerts.",
};

function BrowserPushCard() {
  const config = useQuery({
    queryKey: ["push-config"],
    queryFn: async () => unwrap(await publicApi.GET("/push/config")),
    staleTime: Infinity,
  });
  const publicKey = config.data?.webpush_public_key ?? null;
  const [status, setStatus] = useState<WebPushStatus | null>(null);
  useEffect(() => {
    if (!config.data) return;
    let live = true;
    void webPushStatus(publicKey).then((value) => live && setStatus(value));
    return () => {
      live = false;
    };
  }, [config.data, publicKey]);
  const toggle = useMutation({
    mutationFn: async (enable: boolean) => {
      if (enable && publicKey) await enableWebPush(publicKey);
      else await disableWebPush();
      return webPushStatus(publicKey);
    },
    onSuccess: setStatus,
  });

  return (
    <section className="card stack">
      <h2>This browser</h2>
      {config.error && <p className="error">{errorMessage(config.error)}</p>}
      {status && <p className={status === "on" ? undefined : "muted"}>{STATUS_TEXT[status]}</p>}
      {(status === "off" || status === "on") && (
        <div className="button-row">
          <button
            type="button"
            className={status === "on" ? "secondary" : undefined}
            disabled={toggle.isPending}
            onClick={() => toggle.mutate(status !== "on")}
          >
            {status === "on" ? "Turn off browser alerts" : "Turn on browser alerts"}
          </button>
        </div>
      )}
      {toggle.error && <p className="error">{errorMessage(toggle.error)}</p>}
      <p className="muted">The Android app shows alerts on your phone once notifications are allowed.</p>
    </section>
  );
}
